import renderRadioGroup from 'components/FormControls/radioGroup'
import renderInputField from 'components/FormControls/textInput'
import { Grid, Icon, NetworkIcon, Tooltip, Typography } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, Form, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import {
  ACCESS_FOR_PASSPORT_ORGANIZATION_CREATE,
  GET_ACCOUNT_EXTRA_DATA,
  RESET_ACCOUNT_DATA,
} from 'modules/Services/types'
import ActionButtons from 'modules/Forms/Account/AccessCreate/subcomponents/ActionButtons'
import PageHeader from 'components/PageHeader'
import clsx from 'clsx'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import useNetworkPalette from 'utils/hooks/useNetworkPalette'
import { ROLES } from 'modules/Forms/Account/AccessCreate/Yd/roles'
import {
  validateEmail,
  validationSchema,
} from 'modules/Forms/Account/AccessCreate/Yd/validationSchema'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import { useDispatch, useSelector } from 'react-redux'
import { useNetworksData } from 'modules/Services/hooks/useNetworksData'

let YdAccountAccessCreateForm = ({
  handleSubmit,
  submitting,
  form,
  onClose,
  networkType,
  accountId,
}) => {
  const dispatch = useDispatch()
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()
  const paletteClass = useNetworkPalette(networkType)

  const { extra_data: { passport_organization_name: name = '' } = {} } = useSelector(
    (state) => state['services/network-account'],
  )

  const { networkId } = useNetworksData(networkType)

  useEffect(() => {
    dispatch({
      type: GET_ACCOUNT_EXTRA_DATA,
      payload: {
        networkId,
        accountId,
      },
    })
  }, [accountId, networkId])

  const submit = useCallback(
    (values, dispatch) => {
      const { send_invite_to } = values

      return new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: ACCESS_FOR_PASSPORT_ORGANIZATION_CREATE,
          payload: {
            accountId,
            networkId,
            data: {
              ...values,
              send_invite_to: {
                [validateEmail(send_invite_to) ? 'email' : 'phone']: send_invite_to,
              },
            },
            formName: form,
          },
        })
      }).then(onClose)
    },
    [onClose, accountId, networkId],
  )

  useEffect(() => {
    return () => {
      dispatch({
        type: RESET_ACCOUNT_DATA,
      })
    }
  }, [])

  return (
    <Form
      onSubmit={handleSubmit(submit)}
      noValidate
    >
      <PageHeader className={'margin-bottom-16'}>
        {t(`services::networks.${networkType}`)}
      </PageHeader>
      <Typography
        variant={'Body-20'}
        color={'Label-Secondary'}
        className={clsx({
          ['margin-bottom-64']: !breakpoint.md,
          ['margin-bottom-48']: breakpoint.md,
        })}
      >
        {t('services::forms.account_access_create.passport_organization.description')}
      </Typography>

      <Grid
        direction={'column'}
        gap={12}
        className={'margin-bottom-40'}
      >
        <Typography
          noMargin
          variant={'Body-20'}
          color={'Label-Fifth'}
        >
          {t('services::forms.account_access_create.yd.fields.account_id.label')}
        </Typography>
        <Grid
          alignItems={'center'}
          fluid={false}
          gap={8}
        >
          <NetworkIcon
            size={'s'}
            name={networkType}
            dark={paletteClass !== 'light'}
            rounded
          />
          <Typography
            variant={'Body-15'}
            color={'Label-Fifth'}
          >
            {name}
          </Typography>
        </Grid>
      </Grid>

      <Grid
        direction={'column'}
        gap={12}
        className={'margin-bottom-40'}
      >
        <Typography
          noMargin
          variant={'Body-20'}
          color={'Label-Fifth'}
        >
          {t('services::forms.account_access_create.yd.fields.send_invite_to.label')}
        </Typography>
        <div className={formStyles['row_with-tooltip']}>
          <Field
            fullWidth
            type={'text'}
            name="send_invite_to"
            component={renderInputField}
            t={t}
            label={t('services::forms.account_access_create.yd.fields.send_invite_to.placeholder')}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.account_access_create.yd.fields.send_invite_to.tooltip', {
                phone_number: '79991234567',
              })}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Interface-Sevens'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>
      </Grid>

      <div className={'margin-bottom-16'}>
        <Field
          name="role"
          t={t}
          label={t('services::forms.account_access_create.yd.fields.role.label')}
          component={renderRadioGroup}
          options={ROLES.map((option) => ({
            value: option,
            label: t(
              `services::forms.account_access_create.yd.fields.role.options.${option.toLowerCase()}`,
            ),
          }))}
        />
      </div>

      <ActionButtons
        submitting={submitting}
        onClose={onClose}
      />
    </Form>
  )
}

YdAccountAccessCreateForm = reduxForm({
  form: 'YdAccountAccessCreateForm',
  initialValues: {
    send_invite_to: '',
    role: ROLES[0],
  },
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
})(YdAccountAccessCreateForm)

YdAccountAccessCreateForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  array: PropTypes.object,
  autofill: PropTypes.func,
  form: PropTypes.string,
  onClose: PropTypes.func,
  networkType: PropTypes.string,
  accountId: PropTypes.string,
}

export default YdAccountAccessCreateForm
